import * as React from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import { SliderArrow } from './SliderArrow';
import { Breakpoints, ContentSpacing } from '@estee/elc-base-theme';
import {
    slickArrowTheme,
    slickArrowDisabledTheme,
    slickSlideTheme,
    slickDotButtonFocusTheme
} from '../theme/default-theme';
import { CustomDotsList } from './CustomDotsList';
import { ARROW_DIRECTION } from '@estee/elc-icons';
import { translate } from '@estee/elc-service';
import * as classnames from 'classnames';
import { observer } from 'mobx-react';

export interface ISliderBreakpoints {
    breakpoint: number;
    settings: {
        [key: string]: boolean | number | undefined;
    };
}

export interface ISliderSettings {
    arrows?: boolean;
    dots?: boolean;
    infinite?: boolean;
    lazyload?: boolean;
    responsive?: ISliderBreakpoints[];
    slidesToScroll?: number;
    slidesToShow?: number;
    speed?: number;
    vertical?: boolean;
    initialSlide?: number;
    variableWidth?: boolean;
    centerMode?: boolean;
    accessibility?: boolean;
    afterChange?(index: number): void;
    beforeChange?(previousIndex: number, nextIndex: number): void;
    onSwipe?(direction: string): void;
}

export interface ISliderProps {
    asNavFor?: Slider;
    className?: string;
    innerRef?: string | React.RefObject<Slider> | ((instance: Slider) => Slider);
    items?: JSX.Element[];
    sliderSettings?: ISliderSettings;
    previousSlideTitle?: string;
    nextSlideTitle?: string;
    dataTestId?: string;
}

interface ISliderArrowTranslations {
    translations: {
        previousSlide?: string;
        nextSlide?: string;
    };
}

const SliderWrapper = styled.div`
    box-sizing: border-box;
    width: 100%;
    padding: 0 10px;
    position: relative;
    display: block;

    .slick-slider {
        position: relative;
        display: block;
        box-sizing: border-box;
        user-select: none;
        touch-action: pan-y;
    }

    .slick-list {
        position: relative;
        display: block;
        overflow: hidden;
        margin: 0;
        padding: 0;
    }

    .slick-list:focus {
        outline: none;
    }

    .slick-list.dragging {
        cursor: hand;
    }

    .slick-slider .slick-track,
    .slick-slider .slick-list {
        transform: translate3d(0, 0, 0);
        min-height: 1px;
        min-width: 1px;
    }

    .slick-loading .slick-track {
        visibility: hidden;
        min-height: 1px;
        min-width: 1px;
    }

    .slick-slide {
        display: none;
        float: left;
        height: 100%;
        min-height: 1px;
    }

    [dir='rtl'] .slick-slide {
        float: right;
    }

    .slick-slide img {
        display: block;
    }

    .slick-slide.slick-loading img {
        display: none;
    }

    .slick-slide.dragging img {
        pointer-events: none;
    }

    .slick-initialized .slick-slide {
        display: block;
    }

    .slick-loading .slick-slide {
        visibility: hidden;
    }

    .slick-vertical .slick-slide {
        display: block;
        height: auto;
        border: 1px solid;
        ${slickSlideTheme};
    }

    .slick-vertical .slick-dots li {
        margin: ${ContentSpacing.space4};
    }

    .slick-dots li button:focus {
        ${slickDotButtonFocusTheme}
    }

    .slick-arrow.slick-hidden {
        display: none;
    }

    .slick-arrow {
        ${slickArrowTheme};
    }

    .slick-disabled {
        ${slickArrowDisabledTheme};
    }
`;

const SLIDER_DEFAULT_SETTINGS = {
    arrows: true,
    dots: true,
    infinite: false,
    lazyload: false,
    responsive: [
        {
            breakpoint: Breakpoints.desktop,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 2
            }
        }
    ],
    slidesToScroll: 6,
    slidesToShow: 6,
    speed: 500,
    vertical: false
};

export const getSettings = (settings: ISliderSettings, previous?: string, next?: string) => {
    return {
        prevArrow: (
            <SliderArrow
                direction={
                    settings.vertical
                        ? ARROW_DIRECTION.TOP_DIRECTION
                        : ARROW_DIRECTION.LEFT_DIRECTION
                }
                title={previous}
            />
        ),
        nextArrow: (
            <SliderArrow
                direction={
                    settings.vertical
                        ? ARROW_DIRECTION.BOTTOM_DIRECTION
                        : ARROW_DIRECTION.RIGHT_DIRECTION
                }
                title={next}
            />
        ),
        appendDots: (dots: React.ReactChild[]) => {
            return <CustomDotsList dots={dots} />;
        },
        ...settings
    };
};

@translate(['previousSlide', 'nextSlide'])
@observer
export class SliderView extends React.Component<ISliderProps> {
    public render() {
        const {
            items,
            sliderSettings,
            asNavFor,
            innerRef,
            previousSlideTitle,
            nextSlideTitle,
            dataTestId
        } = this.props;
        const {
            translations: { previousSlide, nextSlide }
        } = this.props as ISliderArrowTranslations;

        const sliderWrapperClassNames = classnames(
            'elc-slider-view-wrapper',
            'js-slider-view-wrapper',
            this.props.className
        );
        const settings = { ...SLIDER_DEFAULT_SETTINGS, ...sliderSettings };
        const previousSlideLabel = previousSlide || previousSlideTitle;
        const nextSlideLabel = nextSlide || nextSlideTitle;
        const allSettings = getSettings(settings, previousSlideLabel, nextSlideLabel);

        return (
            <SliderWrapper className={sliderWrapperClassNames} data-test-id={dataTestId}>
                {items && (
                    <Slider asNavFor={asNavFor} ref={innerRef} {...allSettings}>
                        {items.map(item => (
                            <div key={item.key}>{item}</div>
                        ))}
                    </Slider>
                )}
            </SliderWrapper>
        );
    }
}
